import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Collapse, Descriptions, Drawer, Form, Input, InputNumber, Select, Space, Spin, Switch, Tag, Tooltip, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const MODES = [
    { value: 'none', label: '无' },
    { value: 'onlink', label: '同链路（NDP 代理）' },
    { value: 'routed', label: '路由前缀' },
];

const TASK_STATUS = { 2: ['排队', 'default'], 3: ['执行中', 'processing'], 4: ['失败', 'error'], 5: ['成功', 'success'] };

const okTag = (v) => (v === true ? <Tag color="success">通</Tag> : v === false ? <Tag color="error">不通</Tag> : <Tag>-</Tag>);

const toList = (v) => (Array.isArray(v) ? v : String(v || '').split(/[\s,]+/)).map((s) => String(s).trim()).filter(Boolean);

/** 节点 IPv6 上联：期望配置 + 节点上报 + 最近一次应用任务。 */
const NodeIpv6UplinkDrawer = ({ node, onClose }) => {
    const [form] = Form.useForm();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const mode = Form.useWatch('mode', form);

    const fill = useCallback((cfg, physicalNic) => {
        const c = cfg || {};
        form.setFieldsValue({
            mode: c.mode || 'none',
            iface: c.iface || physicalNic || '',
            vlan: c.vlan || 0,
            address: c.address || '',
            gateway: c.gateway || '',
            prefixes: toList(c.prefixes).join('\n'),
            require_external: !!c.require_external,
        });
    }, [form]);

    const load = useCallback(async (refill = true) => {
        if (!node) return;
        setLoading(true);
        const r = await evmAdminService.getNodeIpv6Uplink(node.id);
        setLoading(false);
        if (r.status !== 'success') {
            message.error(r.msg || '加载失败');
            return;
        }
        setData(r.data || {});
        if (refill) fill(r.data?.config, r.data?.physical_nic || node.physical_nic);
    }, [node, fill]);

    useEffect(() => { if (node) load(true); else setData(null); }, [node, load]);

    const save = async (apply) => {
        let v;
        try { v = await form.validateFields(); } catch (e) { return; }
        setSaving(true);
        const r = await evmAdminService.saveNodeIpv6Uplink(node.id, {
            mode: v.mode,
            iface: v.iface || '',
            vlan: v.vlan || 0,
            address: v.mode === 'none' ? '' : (v.address || ''),
            gateway: v.mode === 'none' ? '' : (v.gateway || ''),
            prefixes: v.mode === 'routed' ? toList(v.prefixes) : [],
            require_external: v.require_external ? 1 : 0,
            apply: apply ? 1 : 0,
        });
        setSaving(false);
        if (r.status === 'success') {
            message.success(apply ? '已提交应用' : '已保存');
            setData(r.data || data);
            if (r.data?.config) fill(r.data.config, r.data.physical_nic);
        } else {
            message.error(r.msg || '失败');
        }
    };

    const rep = data?.report;
    const task = data?.task;
    const ts = task ? TASK_STATUS[Number(task.status)] || [String(task.status), 'default'] : null;

    return (
        <Drawer
            title={`IPv6 上联 - ${node?.name || ''}`}
            width={560}
            open={!!node}
            onClose={onClose}
            destroyOnClose
            extra={<Button size="small" icon={<ReloadOutlined />} onClick={() => load(false)} />}
            footer={
                <Space style={{ float: 'right' }}>
                    <Button loading={saving} onClick={() => save(false)}>保存</Button>
                    <Button type="primary" loading={saving} onClick={() => save(true)}>保存并应用</Button>
                </Space>
            }
        >
            <Spin spinning={loading}>
                {task && (
                    <Alert
                        style={{ marginBottom: 12 }}
                        type={ts[1] === 'error' ? 'error' : ts[1] === 'success' ? 'success' : 'info'}
                        showIcon
                        message={<Space wrap><span>上次应用</span><Tag color={ts[1]}>{ts[0]}</Tag><span>{task.end_time || task.start_time || ''}</span></Space>}
                        description={task.msg || undefined}
                    />
                )}

                <Descriptions
                    size="small"
                    column={2}
                    bordered
                    title={<Space>节点检测{data?.reported_at && <span style={{ fontWeight: 'normal', fontSize: 12, color: '#999' }}>{data.reported_at}</span>}</Space>}
                    extra={data?.suggested && (
                        <Button size="small" onClick={() => fill(data.suggested, data.physical_nic)}>使用检测值</Button>
                    )}
                    style={{ marginBottom: 16 }}
                >
                    {rep ? (
                        <>
                            <Descriptions.Item label="托管">
                                {rep.managed ? <Tag color="blue">已托管</Tag> : <Tag>未托管</Tag>}
                                {rep.manager && <Tag>{rep.manager}</Tag>}
                            </Descriptions.Item>
                            <Descriptions.Item label="接口">
                                {rep.iface || '-'}{rep.vlan ? ` (${rep.parent || ''}.${rep.vlan})` : ''}
                            </Descriptions.Item>
                            <Descriptions.Item label="地址" span={2}>{toList(rep.addresses).join(', ') || '-'}</Descriptions.Item>
                            <Descriptions.Item label="网关" span={2}>{rep.gateway || '-'}</Descriptions.Item>
                            {toList(rep.prefixes).length > 0 && (
                                <Descriptions.Item label="前缀" span={2}>{toList(rep.prefixes).join(', ')}</Descriptions.Item>
                            )}
                            <Descriptions.Item label="转发">{rep.forwarding ? <Tag color="success">开</Tag> : <Tag>关</Tag>}</Descriptions.Item>
                            <Descriptions.Item label="网关/外网">{okTag(rep.gw_ok)}{okTag(rep.ext_ok)}</Descriptions.Item>
                            {toList(rep.foreign_files).length > 0 && (
                                <Descriptions.Item label={<Tooltip title="手工配置，托管配置验证通过后移走">手工文件</Tooltip>} span={2}>
                                    {toList(rep.foreign_files).map((f) => <div key={f}><code>{f}</code></div>)}
                                </Descriptions.Item>
                            )}
                        </>
                    ) : (
                        <Descriptions.Item label="状态" span={2}>未上报</Descriptions.Item>
                    )}
                </Descriptions>

                <Form form={form} layout="vertical" initialValues={{ mode: 'none', vlan: 0 }}>
                    <Form.Item name="mode" label="模式" rules={[{ required: true }]}>
                        <Select options={MODES} />
                    </Form.Item>
                    {mode && mode !== 'none' && (
                        <>
                            <Form.Item name="address" label="主机地址" tooltip="含前缀长度，如 2604:db8::2/126" rules={[{ required: true, message: '必填' }]}>
                                <Input placeholder="2001:db8::2/64" />
                            </Form.Item>
                            <Form.Item name="gateway" label="网关" rules={[{ required: true, message: '必填' }]}>
                                <Input placeholder="2001:db8::1" />
                            </Form.Item>
                            {mode === 'routed' && (
                                <Form.Item name="prefixes" label="路由前缀" tooltip="服务商路由到本机的前缀，每行一个" rules={[{ required: true, message: '必填' }]}>
                                    <Input.TextArea rows={2} placeholder="2001:db8:100::/48" />
                                </Form.Item>
                            )}
                        </>
                    )}
                    <Collapse
                        ghost
                        size="small"
                        items={[{
                            key: 'adv',
                            label: '高级',
                            children: (
                                <>
                                    <Form.Item name="iface" label="上联网卡">
                                        <Input placeholder={data?.physical_nic || node?.physical_nic || 'eth0'} />
                                    </Form.Item>
                                    <Form.Item name="vlan" label="VLAN" tooltip="0 = 不使用">
                                        <InputNumber min={0} max={4094} style={{ width: '100%' }} />
                                    </Form.Item>
                                    <Form.Item name="require_external" label="外网不通则回滚" valuePropName="checked">
                                        <Switch />
                                    </Form.Item>
                                </>
                            ),
                        }]}
                    />
                </Form>
            </Spin>
        </Drawer>
    );
};

export default NodeIpv6UplinkDrawer;
